import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import {
  MapPin, Sparkle, Camera, Feather, Leaf,
  Bug, Sun, Drop, Mountains, TreeEvergreen, Plant,
  Wind, Fish, Warning, Sparkle as SparkleIcon, ArrowRight,
  BookOpen,
} from "@phosphor-icons/react";
import TrustBadges from "@/components/TrustBadges";
import ShareButton from "@/components/ShareButton";
import ChannelArticleModal from "@/components/ChannelArticleModal";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import { CHANNEL_ARTICLES, OBSERVATIONS_INTRO } from "@/data/observationChannels";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

// Editorial channels per NaturePulse content brief. Each channel maps to
// one or more canonical NEWS_CATEGORIES slugs on nature_stories and carries
// an editorial excerpt shown as a subtitle on the tile and expanded when the
// channel is selected.
const CHANNELS = [
  { key: "plants",       label: "Plants & Vegetation",       Icon: Leaf,           emoji: "🌱", slugs: ["plants"],
    excerpt: "Discover and document the plants growing around us—from wildflowers and grasses to herbs, shrubs and native vegetation. Record flowering, fruiting, new growth, disease, unusual seasonal changes and changing plant populations with photos, stories, dates and locations." },
  { key: "trees",        label: "Trees & Forests",           Icon: TreeEvergreen,  emoji: "🌳", slugs: ["trees"],
    excerpt: "Trees and forests tell long stories about climate, seasons and the health of our environment. Document individual trees, forests, flowering and fruiting, leaf changes, storm damage, disease, wildfires, deforestation, restoration and the changing landscapes they create." },
  { key: "agriculture",  label: "Agriculture & Edibles",     Icon: Plant,          emoji: "🌾", slugs: ["agriculture","crops","fruits","vegetables"],
    excerpt: "Follow the foods growing around the world—from backyard gardens and family farms to major agricultural regions. Share observations about fruits, vegetables, grains, herbs, crops, planting, flowering, harvests, pests, drought, weather damage, food abundance and changing growing seasons." },
  { key: "birds",        label: "Birds",                     Icon: Feather,        emoji: "🐦", slugs: ["birds"],
    excerpt: "Every bird sighting can help tell a larger story. Record local and migratory birds, nesting, feeding, breeding, arrival and departure dates, unusual sightings and population changes. Photos, audio recordings and repeated observations can help document how bird life changes over time." },
  { key: "wildlife",     label: "Animals & Wildlife",        Icon: Sparkle,        emoji: "🐾", slugs: ["wildlife","mammals"],
    excerpt: "Observe the animals sharing our communities, forests, farms and wilderness. Document mammals, reptiles, amphibians and other wildlife—their locations, behaviors, breeding, migration, habitat changes, unusual appearances and interactions with people and the environment." },
  { key: "insects",      label: "Insects & Pollinators",     Icon: Bug,            emoji: "🦋", slugs: ["insects"],
    excerpt: "Small creatures can reveal enormous environmental changes. Record butterflies, bees, moths, dragonflies, beetles and other insects, including pollination activity, migrations, seasonal appearances, population changes, invasive species and agricultural pests." },
  { key: "aquatic",      label: "Aquatic Life",              Icon: Fish,           emoji: "🐟", slugs: ["fisheries","oceans"],
    excerpt: "Explore life beneath and around the water — freshwater and marine. Document fish, shellfish, amphibians, aquatic plants, coral reefs, coastal wildlife, tides and shorelines. Record spawning, migrations, unusual concentrations, population changes, fish kills, invasive species and changes in aquatic and coastal habitats." },
  { key: "sky",          label: "Weather & Sky",             Icon: Sun,            emoji: "🌦", slugs: ["weather","air-quality"],
    excerpt: "Become a local observer of the atmosphere. Record rain, snow, thunderstorms, lightning, hail, clouds, fog, wind, heat waves, cold spells, sunsets, sunrises, rainbows and unusual sky events. Today's ordinary weather observation can become tomorrow's climate history." },
  { key: "water",        label: "Water · Rivers & Floods",   Icon: Drop,           emoji: "💧", slugs: ["freshwater","floods","drought"],
    excerpt: "Water shapes communities and ecosystems. Record rivers, streams, lakes, ponds, springs, wetlands and reservoirs, including water levels, flooding, drought, changing flows, pollution, erosion and unusual water conditions. Repeated observations can reveal important long-term changes." },
  { key: "land",         label: "Land & Geological Changes", Icon: Mountains,      emoji: "⛰", slugs: ["geological","landslide","soil"],
    excerpt: "Our planet is constantly reshaping itself. Document mountains, hills, rocks, cliffs, soil, erosion, landslides, sinkholes, earthquakes, volcanic activity and changes in the landscape. Photos from the same locations over time can create a remarkable record of Earth's movement." },
  { key: "hazards",      label: "Roads & Environmental Hazards", Icon: Warning,     emoji: "⚠️", slugs: ["wildfires","pollution","other-natural-event","landslide"],
    excerpt: "Document road conditions and events that suddenly affect people, wildlife and landscapes — potholes, flooded roads, fallen trees, landslides, wildfires, storm damage, tornadoes, lightning damage and other natural impacts. Share observations only when it is safe to do so and never place yourself in danger to document an event." },
  { key: "seasonal",     label: "Seasonal & Climate Changes",Icon: Wind,           emoji: "🌍", slugs: ["climate","records","unusual"],
    excerpt: "Help document how the seasons are changing around the world. Record first flowers, first fruits, leaf emergence, autumn colors, migrations, snowfall, rainfall, drought, heat, ice and changing growing seasons. One observation is a moment; repeated observations over years can reveal a pattern." },
];

const ADOPT_TAG = "adopt-a-place";

function FlagshipCard({ s }) {
  return (
    <Link
      to={`/stories/${s.id}`}
      data-testid={`flagship-${s.id}`}
      className="group block rounded-2xl border border-black/10 bg-white overflow-hidden hover:border-[color:var(--np-brand,#B85F2A)] transition-colors"
    >
      <div className="p-5 md:p-6 flex flex-col gap-3">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          / Flagship article · {s.category}
        </div>
        <div className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tight">{s.title}</div>
        <div className="text-sm text-[#3A3830]/80 line-clamp-2">{s.summary}</div>
        <div className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] group-hover:gap-2 transition-all">
          Read the full essay <ArrowRight size={12} weight="bold" />
        </div>
      </div>
    </Link>
  );
}

function ObservationCard({ o }) {
  const cover = o.cover_image || o.media?.[0]?.url;
  const rp = o.submitter?.name || o.reporter?.public_alias || "Observer";
  const when = (o.observed_at || o.event_date || o.created_at || "").slice(0, 10);
  return (
    <div
      className="group relative rounded-2xl overflow-hidden bg-white border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] transition-colors flex flex-col"
      data-testid={`obs-card-${o.id}`}
    >
      <Link to={`/stories/${o.id}`} className="flex-1 flex flex-col">
        {cover ? (
          <div className="aspect-[4/3] bg-black/5 overflow-hidden">
            <img src={abs(cover)} alt={o.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" />
          </div>
        ) : (
          <div className="aspect-[4/3] bg-gradient-to-br from-[#F5EBDC] to-[#E4A15C]/20 flex items-center justify-center">
            <Leaf size={42} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/40" />
          </div>
        )}
        <div className="p-4 flex-1 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center rounded-full px-2 py-0.5 border font-mono-tech text-[9px] tracking-[0.22em] uppercase bg-[#F5EBDC] text-[#241209] border-[#E4A15C]/30">
              {o.category || "observation"}
            </span>
            <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">{when}</span>
          </div>
          <div className="font-serif-display text-lg leading-tight line-clamp-2">{o.title}</div>
          {(o.location?.city || o.location?.state || o.location?.country) && (
            <div className="text-xs text-[#3A3830] flex items-center gap-1 line-clamp-1">
              <MapPin size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
              {[o.location.city, o.location.state, o.location.country].filter(Boolean).join(", ")}
            </div>
          )}
          {o.summary && <div className="text-xs text-[#3A3830]/70 line-clamp-2 mt-1">{o.summary}</div>}
          <TrustBadges labels={o.trust_labels} size="xs" className="mt-1" />
          <div className="mt-auto pt-2 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">
            {rp}
          </div>
        </div>
      </Link>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <ShareButton
          url={`${window.location.origin}/stories/${o.id}`}
          title={o.title}
          text={[o.location?.city, o.location?.country].filter(Boolean).join(", ")}
          variant="icon"
          testid={`obs-card-share-${o.id}`}
        />
      </div>
    </div>
  );
}

export default function Observations() {
  const [items, setItems] = useState([]);
  const [flagships, setFlagships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sp, setSp] = useSearchParams();
  const channel = sp.get("channel") || "";
  const adopt = sp.get("adopt") === "1";

  const channelDef = useMemo(() => CHANNELS.find((c) => c.key === channel), [channel]);
  const [openArticleKey, setOpenArticleKey] = useState(null);
  const openArticle = openArticleKey
    ? (openArticleKey === "__intro"
        ? { emoji: "🌿", title: "Observations", subtitle: OBSERVATIONS_INTRO.title,
            lede: OBSERVATIONS_INTRO.body, sections: [], outro: null, slug: "intro" }
        : { ...CHANNEL_ARTICLES[openArticleKey], slug: openArticleKey })
    : null;
  const heroSpeakText = `${OBSERVATIONS_INTRO.title}\n\n${OBSERVATIONS_INTRO.body}`;

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("limit", "60");
    if (channelDef) {
      // request each configured slug — backend supports single category filter;
      // we'll filter client-side across allowed slugs after fetching.
      params.set("category_in", channelDef.slugs.join(","));
    }
    Promise.all([
      http.get(`/nature/stories/list/all?${params}`).catch(() => ({ data: { stories: [] } })),
      http.get(`/nature/stories/list/all?story_type=article&limit=6`).catch(() => ({ data: { stories: [] } })),
    ]).then(([res, artRes]) => {
      const stories = res.data?.stories || [];
      let filtered = stories.filter((s) => s.story_type !== "article" && s.story_type !== "news_link");
      if (channelDef) {
        filtered = filtered.filter((s) => channelDef.slugs.includes(s.category));
      }
      if (adopt) {
        filtered = filtered.filter((s) => (s.tags || []).includes(ADOPT_TAG));
      }
      setItems(filtered);
      const flagshipList = (artRes.data?.stories || []).filter((s) => s.is_flagship);
      if (channelDef) {
        setFlagships(flagshipList.filter((s) => channelDef.slugs.includes(s.category)));
      } else {
        setFlagships(flagshipList.slice(0, 3));
      }
    }).finally(() => setLoading(false));
  }, [channelDef, adopt]);

  const setChannel = (k) => setSp((p) => { const n = new URLSearchParams(p); if (k) n.set("channel", k); else n.delete("channel"); return n; });

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#151312)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="observations-page">
      {/* HERO — the "What did you notice in nature today?" call to action */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
          / Observation network · living record
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.92] tracking-tighter max-w-4xl">
          What did you notice in nature today?
        </h1>
        <p className="mt-6 font-serif-display text-base md:text-lg text-[#3A3830] max-w-3xl leading-relaxed">
          You don't have to be a scientist. Did a flower bloom? Did you see a bird, butterfly or animal? Is your garden producing fruit? Did heavy rain flood a road? Is a stream unusually low? Did you notice smoke, snow, unusual clouds, erosion, a fallen tree or changing leaves?
          <br /><br />
          Photograph it. Describe what you saw. Let NaturePulse record the date, time and place.
          <br /><br />
          <span className="italic text-[color:var(--np-brand,#B85F2A)]">Your observation may seem ordinary today. Recorded year after year, it could become valuable evidence of how our planet is changing.</span>
        </p>
        <div className="mt-8 flex flex-wrap gap-3" data-testid="obs-hero-actions">
          <Link
            to="/submit"
            data-testid="obs-add-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-6 py-3 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Camera size={14} weight="bold" /> Add an observation
          </Link>
          <Link
            to="/adopt-a-place"
            data-testid="obs-adopt-cta"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-mono-tech text-[11px] tracking-[0.28em] uppercase border border-black/20 hover:bg-white/60"
          >
            <SparkleIcon size={14} weight="bold" /> Adopt a place to observe
          </Link>
        </div>
        <div className="mt-6" data-testid="obs-hero-tools">
          <ListenTranslateBar text={heroSpeakText} testidPrefix="obs-hero" />
        </div>
      </section>

      {/* Different Ways to Observe Nature — intro editorial block */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14">
        <div className="rounded-3xl border border-black/10 bg-white/70 p-6 md:p-8" data-testid="obs-intro-card">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
            {OBSERVATIONS_INTRO.eyebrow}
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
            {OBSERVATIONS_INTRO.title}
          </h2>
          <p className="mt-4 font-serif-display text-[15px] md:text-base leading-relaxed text-[#241209] whitespace-pre-line line-clamp-6">
            {OBSERVATIONS_INTRO.body}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setOpenArticleKey("__intro")}
              data-testid="obs-intro-read-more"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#241209] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              <BookOpen size={12} weight="fill" /> Read more <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* 12 CHANNELS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-16">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif-display text-2xl md:text-3xl tracking-tight">Twelve ways to observe.</h2>
          {channel && (
            <button
              onClick={() => setChannel("")}
              data-testid="obs-channel-clear"
              className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]/70 hover:text-[#241209]"
            >
              ↺ Show all channels
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="obs-channels">
          {CHANNELS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setChannel(key === channel ? "" : key)}
              data-testid={`obs-channel-${key}`}
              className={`text-left rounded-xl border p-3 transition ${
                channel === key
                  ? "border-[#B85F2A] bg-[#241209] text-[#F5EBDC]"
                  : "border-black/10 bg-white hover:border-black/30"
              }`}
            >
              <Icon size={18} weight="fill" className={channel === key ? "text-[#E4A15C]" : "text-[color:var(--np-brand,#B85F2A)]"} />
              <div className="mt-2 font-serif-display text-sm leading-tight">{label}</div>
            </button>
          ))}
        </div>
        {channelDef && (
          <div
            className="mt-4 rounded-2xl border border-black/10 bg-white/70 p-5 md:p-6"
            data-testid={`obs-channel-excerpt-${channelDef.key}`}
          >
            <div className="flex items-start gap-3">
              <channelDef.Icon size={24} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] shrink-0 mt-1" />
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  / {channelDef.label}
                </div>
                <p className="mt-1 font-serif-display text-[15px] md:text-base leading-relaxed text-[#241209] max-w-4xl">
                  {channelDef.excerpt}
                </p>
                {CHANNEL_ARTICLES[channelDef.key] && (
                  <div className="mt-3">
                    <button
                      onClick={() => setOpenArticleKey(channelDef.key)}
                      data-testid={`obs-channel-read-more-${channelDef.key}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#241209] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
                    >
                      <BookOpen size={12} weight="fill" /> Read more <ArrowRight size={11} weight="bold" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* FLAGSHIP FEATURED ARTICLES */}
      {flagships.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14" data-testid="obs-flagship-row">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
            / Featured reading
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {flagships.slice(0, 3).map((s) => <FlagshipCard key={s.id} s={s} />)}
          </div>
        </section>
      )}

      {/* OBSERVATION GRID */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif-display text-2xl md:text-3xl tracking-tight">
            {channelDef ? channelDef.label : "The living feed"}
            {adopt && " · Adopt-a-Place"}
          </h2>
          <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]/70">
            {loading ? "loading…" : `${items.length} observation${items.length === 1 ? "" : "s"}`}
          </span>
        </div>
        {loading ? (
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-black/20 p-10 text-center">
            <Sparkle size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl">No observations yet in this channel.</div>
            <div className="mt-1 text-sm text-[#3A3830]">Be the first — hit "Add an observation" above.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="obs-grid">
            {items.map((o) => <ObservationCard key={o.id} o={o} />)}
          </div>
        )}
      </section>

      {openArticle && (
        <ChannelArticleModal article={openArticle} onClose={() => setOpenArticleKey(null)} />
      )}
    </main>
  );
}
